
import { useState, useEffect, useCallback, useRef } from 'react';
import stationsData from '../data/stations.json';
import airportsData from '../data/airports.json';

const GOOGLE_PLACES_LIMIT = 5;
const LOCAL_LIMIT = 5;
const TOTAL_LIMIT = 15;

const POPULAR_LOCATIONS = [
    { name: "Indira Gandhi Intl Airport", code: "DEL", city: "Delhi", type: "airport", label: "Delhi (DEL)", subLabel: "IGI Airport", lat: 28.5562, lng: 77.1000 },
    { name: "New Delhi", code: "NDLS", city: "Delhi", type: "station", label: "New Delhi (NDLS)", subLabel: "Railway Station", lat: 28.6141, lng: 77.2144 },
    { name: "Chhatrapati Shivaji Intl", code: "BOM", city: "Mumbai", type: "airport", label: "Mumbai (BOM)", subLabel: "International Airport", lat: 19.0887, lng: 72.8679 },
    { name: "Mumbai Central", code: "MMCT", city: "Mumbai", type: "station", label: "Mumbai Central (MMCT)", subLabel: "Railway Station", lat: 18.9698, lng: 72.8193 },
    { name: "Kempegowda Intl Airport", code: "BLR", city: "Bangalore", type: "airport", label: "Bangalore (BLR)", subLabel: "International Airport", lat: 13.1989, lng: 77.7068 },
    { name: "KSR Bengaluru City", code: "SBC", city: "Bengaluru", type: "station", label: "Bengaluru (SBC)", subLabel: "Railway Station", lat: 12.9774, lng: 77.5728 },
    { name: "Chennai Central", code: "MAS", city: "Chennai", type: "station", label: "Chennai Central (MAS)", subLabel: "Railway Station", lat: 13.0827, lng: 80.2707 },
    { name: "Netaji Subhash Chandra Bose Intl", code: "CCU", city: "Kolkata", type: "airport", label: "Kolkata (CCU)", subLabel: "International Airport", lat: 22.6547, lng: 88.4467 },
    { name: "Pune Junction", code: "PUNE", city: "Pune", type: "station", label: "Pune Jn (PUNE)", subLabel: "Railway Station", lat: 18.5289, lng: 73.8744 },
    { name: "Hyderabad Deccan", code: "HYB", city: "Hyderabad", type: "station", label: "Hyderabad (HYB)", subLabel: "Railway Station", lat: 17.3916, lng: 78.4684 }
];

export const useTravelAutocomplete = () => {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Cache for Google results
    const googleCache = useRef(new Map());
    const autocompleteService = useRef(null);

    useEffect(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
    }, []);

    const calculateRank = (item, lowerQuery) => {
        const name = item.name || item.stationName || item.airportName || '';
        const code = item.code || item.stationCode || item.airportCode || '';
        const city = item.city || '';

        const lowerCode = code.toLowerCase();
        const lowerName = name.toLowerCase();
        const lowerCity = city.toLowerCase();

        if (lowerCode === lowerQuery) return 0; // Exact Code
        if (lowerCode.startsWith(lowerQuery)) return 1; // Code Prefix
        if (lowerName.startsWith(lowerQuery)) return 2; // Name Prefix
        if (lowerCity && lowerCity.startsWith(lowerQuery)) return 3; // City Prefix
        return 4; // Contains
    };

    const searchLocal = (query) => {
        const lowerQuery = query.toLowerCase();

        const processItems = (items, type) => {
            return items
                .map(item => ({ ...item, rank: calculateRank(item, lowerQuery) }))
                .filter(item => {
                    if (item.rank < 4) return true;
                    // For rank 4 (contains), double check logic if not covered by prefixes
                    const name = item.name || item.stationName || item.airportName || '';
                    const code = item.code || item.stationCode || item.airportCode || '';
                    const city = item.city || '';

                    const lowerCode = code.toLowerCase();
                    const lowerName = name.toLowerCase();
                    const lowerCity = city.toLowerCase();

                    return lowerCode.includes(lowerQuery) || lowerName.includes(lowerQuery) || lowerCity.includes(lowerQuery);
                })
                .sort((a, b) => {
                    if (a.rank !== b.rank) return a.rank - b.rank;
                    const nameA = a.name || a.stationName || a.airportName || '';
                    const nameB = b.name || b.stationName || b.airportName || '';
                    return nameA.localeCompare(nameB);
                })
                .slice(0, LOCAL_LIMIT)
                .map(({ rank, ...rest }) => {
                    const name = rest.name || rest.stationName || rest.airportName || '';
                    const code = rest.code || rest.stationCode || rest.airportCode || '';
                    const city = rest.city || '';

                    // Extract lat/lng from various potential schemas
                    let lat = rest.lat;
                    let lng = rest.lng;

                    if (rest.lat_long && Array.isArray(rest.lat_long)) {
                        lat = rest.lat_long[0];
                        lng = rest.lat_long[1];
                    }

                    return {
                        ...rest,
                        type,
                        name, // Ensure 'name' and 'code' are available for the rest of the app
                        code,
                        city,
                        lat,
                        lng,
                        label: `${name} (${code})`,
                        subLabel: city || (type === 'station' ? 'Railway Station' : 'Airport')
                    };
                });
        };

        const stations = processItems(stationsData, 'station');
        const airports = processItems(airportsData, 'airport');

        return { stations, airports };
    };

    const searchGoogle = useCallback((query) => {
        return new Promise((resolve) => {
            if (!window.google || !window.google.maps || !window.google.maps.places) {
                console.warn("Google Maps API not loaded");
                resolve([]);
                return;
            }

            if (!autocompleteService.current) {
                try {
                    autocompleteService.current = new window.google.maps.places.AutocompleteService();
                } catch (err) {
                    console.error("Failed to initialize AutocompleteService", err);
                    resolve([]);
                    return;
                }
            }

            if (googleCache.current.has(query)) {
                resolve(googleCache.current.get(query));
                return;
            }

            const request = {
                input: query,
                // types: ['(cities)'], // Removed to allow airports and stations
                componentRestrictions: { country: 'in' } // Restrict to India
            };

            autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    const results = predictions.slice(0, GOOGLE_PLACES_LIMIT).map(p => {
                        let type = 'google';
                        const lowerDesc = p.description.toLowerCase();
                        const types = p.types || [];

                        if (types.includes('airport') || lowerDesc.includes('airport')) {
                            type = 'airport';
                        } else if (
                            types.includes('train_station') ||
                            types.includes('transit_station') ||
                            lowerDesc.includes('railway station') ||
                            lowerDesc.includes('junction') ||
                            lowerDesc.includes('terminus') ||
                            lowerDesc.includes('cantt')
                        ) {
                            type = 'station';
                        }

                        return {
                            description: p.description,
                            place_id: p.place_id,
                            type: type, // specific type for icon
                            label: p.structured_formatting?.main_text || p.description,
                            subLabel: p.structured_formatting?.secondary_text || 'City',
                            structured_formatting: p.structured_formatting,
                            original_types: p.types
                        };
                    });
                    googleCache.current.set(query, results);
                    resolve(results);
                } else {
                    resolve([]);
                }
            });
        });
    }, []);

    const search = useCallback(async (query) => {
        if (!query || query.length < 1) {
            setSuggestions(POPULAR_LOCATIONS);
            setLoading(false);
            return;
        }

        if (query.length < 2) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Local Search with Ranking
            const { stations, airports } = searchLocal(query);

            // Combine and Re-rank
            // Since we want to re-rank the combined list to mix stations and airports based on relevance
            let combinedLocal = [...stations, ...airports];
            const lowerQuery = query.toLowerCase();

            // Re-calculate rank for sorting combined list (though they are top 5 of each, mixing them needs sorting)
            combinedLocal = combinedLocal.map(item => ({
                ...item,
                rank: calculateRank({
                    code: item.code, // Extract original code from label? No, we kept properties in rest
                    name: item.name,
                    city: item.city
                }, lowerQuery)
            })).sort((a, b) => {
                if (a.rank !== b.rank) return a.rank - b.rank;
                return a.label.localeCompare(b.label);
            });

            // If combined results < 15, fetch Google
            let finalSuggestions = combinedLocal;

            if (finalSuggestions.length < TOTAL_LIMIT) {
                let googleResults = [];
                try {
                    googleResults = await searchGoogle(query);
                } catch (err) {
                    console.warn('Google search failed', err);
                }

                const remaining = TOTAL_LIMIT - finalSuggestions.length;
                if (remaining > 0) {
                    finalSuggestions = [...finalSuggestions, ...googleResults.slice(0, remaining)];
                }
            }

            setSuggestions(finalSuggestions);

        } catch (err) {
            console.error('Autocomplete error:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [searchGoogle]);

    // Debounce wrapper
    const debounceRef = useRef(null);
    const debouncedSearch = useCallback((query) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            search(query);
        }, 300);
    }, [search]);

    return {
        suggestions,
        popularSuggestions: POPULAR_LOCATIONS,
        loading,
        error,
        search: debouncedSearch
    };
};
